import React, { useState } from "react";
import "./LoginPage.css";
import { useNavigate } from "react-router-dom";

const ROLES = [
  {
    id: "admin",
    label: "Admin",
    subtitle: "Full access",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2L4 5v6c0 5 4 9 8 11 4-2 8-6 8-11V5l-8-3z"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.5 11.5l2 2 3-3"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "manager",
    label: "Manager",
    subtitle: "Team & reports",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" />
        <path
          d="M10.5 10.5l3-1.5-1.5 3-3 1.5 1.5-3z"
          fill="currentColor"
          opacity="0.95"
        />
      </svg>
    ),
  },
  {
    id: "employee",
    label: "Employee",
    subtitle: "Daily tasks",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect
          x="3"
          y="8"
          width="18"
          height="11"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <path
          d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        <path
          d="M3 13h18"
          stroke="currentColor"
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

export default function LoginPage() {
  const [activeRole, setActiveRole] = useState("admin");
  const [formData, setFormData] = useState({
    admin: { email: "", password: "" },
    manager: { email: "", password: "" },
    employee: { email: "", password: "" },
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (role, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [role]: { ...prev[role], [field]: value },
    }));
  };

  const handleSubmit = async (e, role) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            role,
            email: formData[role].email,
            password: formData[role].password,
          }),
        }
      );

      const data = await res.json();
      console.log("[login] response:", { status: res.status, body: data });

      if (!res.ok) throw new Error(data.error || "Login failed");

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      if (data.user.role === "admin") navigate("/admin");
      else if (data.user.role === "manager") navigate("/manager");
      else if (data.user.role === "employee") navigate("/employee");
      else navigate("/");
    } catch (err) {
      alert(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = (role) => {
    setFormData((p) => ({
      ...p,
      [role]: { email: "", password: "" },
    }));
  };

  return (
    <div className="login-container">
      <div className="login-card" role="region" aria-label="Login card">
        <header className="card-header">
          <div className="brand">
            <div className="logo" aria-hidden>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <rect
                  x="2"
                  y="2"
                  width="20"
                  height="20"
                  rx="6"
                  fill="currentColor"
                  opacity="0.06"
                />
                <circle
                  cx="12"
                  cy="9"
                  r="3"
                  fill="currentColor"
                  opacity="0.12"
                />
              </svg>
            </div>
            <div className="brand-text">
              <h1>Welcome back</h1>
              <p className="muted">Sign in to continue</p>
            </div>
          </div>

          <div className="card-strap">Secure access</div>
        </header>

        <div className="card-body">
          <div className="panels">
            {ROLES.map((r) =>
              activeRole === r.id ? (
                <section
                  key={r.id}
                  className="panel active"
                  aria-hidden={activeRole !== r.id}
                >
                  <div className="panel-top">
                    <div className="role-icon" aria-hidden>
                      {r.icon}
                    </div>
                    <div className="role-meta">
                      <h2 className="role-name">{r.label}</h2>
                      <div className="role-sub">{r.subtitle}</div>
                    </div>
                  </div>

                  <form
                    className="role-form"
                    onSubmit={(e) => handleSubmit(e, r.id)}
                    autoComplete="on"
                  >
                    <label className="floating-label" htmlFor={`${r.id}-email`}>
                      <input
                        id={`${r.id}-email`}
                        name={`${r.id}-email`}
                        type="text"
                        value={formData[r.id].email}
                        onChange={(e) =>
                          handleChange(r.id, "email", e.target.value)
                        }
                        required
                        placeholder=" " // 👈 single space placeholder
                      />
                      <span>Email</span>
                    </label>

                    <label
                      className="floating-label"
                      htmlFor={`${r.id}-password`}
                    >
                      <input
                        id={`${r.id}-password`}
                        name={`${r.id}-password`}
                        type="password"
                        value={formData[r.id].password}
                        onChange={(e) =>
                          handleChange(r.id, "password", e.target.value)
                        }
                        required
                        placeholder=" " // 👈 single space placeholder
                      />
                      <span>Password</span>
                    </label>

                    <div className="form-actions">
                      <button
                        className="btn-primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? "Signing in..." : "Sign in"}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => clearForm(r.id)}
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                </section>
              ) : null
            )}
          </div>
        </div>
      </div>

      <div className="role-switch-container" aria-hidden={false}>
        <div className="role-switch" role="tablist" aria-label="Select role">
          {ROLES.map((r) => (
            <button
              key={r.id}
              role="tab"
              aria-selected={activeRole === r.id}
              className={`role-btn ${activeRole === r.id ? "selected" : ""}`}
              onClick={() => setActiveRole(r.id)}
            >
              <span className="btn-icon" aria-hidden>
                {r.icon}
              </span>
              <span className="btn-label">{r.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
